import { CHARACTER_VISIBILITY } from "@next-platform/client-common-core";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { GroupMembersChatBot } from "@/components/MembersGroupChat";
import { IDraftGroupCharacter } from "@/types";

interface GroupChatFormContextType {
  draftId: string | undefined;
  setDraftId: (id: string | undefined) => void;
  formData: Partial<IDraftGroupCharacter>;
  updateFormData: (data: Partial<IDraftGroupCharacter>) => void;
  referenceValues: Record<string, unknown>;
  setReferenceValues: (values: Record<string, unknown>) => void;
  members: GroupMembersChatBot[];
  setMembers: (members: GroupMembersChatBot[]) => void;
  groupVisibility: string;
  setGroupVisibility: (visibility: string) => void;
  resetForm: () => void;
  hasData: boolean;
}

const GroupChatFormContext = createContext<GroupChatFormContextType | undefined>(undefined);

interface GroupChatFormProviderProps {
  children: ReactNode;
  initialData?: Partial<IDraftGroupCharacter>;
}

export function GroupChatFormProvider({ children, initialData = {} }: GroupChatFormProviderProps) {
  const [formData, setFormData] = useState<Partial<IDraftGroupCharacter>>(initialData);
  const [draftId, setDraftIdState] = useState<string | undefined>(initialData.id);
  const [referenceValues, setReferenceValuesState] = useState<Record<string, unknown>>({});
  const [groupVisibility, setGroupVisibilityState] = useState(initialData.visibility || CHARACTER_VISIBILITY.PRIVATE);

  const members = formData.members || [];

  useEffect(() => {
    if (formData.visibility && formData.visibility !== groupVisibility) {
      setGroupVisibilityState(formData.visibility);
    }
  }, [formData.visibility, groupVisibility]);

  const updateFormData = useCallback((data: Partial<IDraftGroupCharacter>) => {
    setFormData((prev: Partial<IDraftGroupCharacter>) => ({
      ...prev,
      ...data,
    }));
  }, []);

  const setMembers = useCallback((newMembers: GroupMembersChatBot[]) => {
    setFormData((prev: Partial<IDraftGroupCharacter>) => ({
      ...prev,
      members: newMembers,
    }));
  }, []);

  const setDraftId = useCallback((id: string | undefined) => {
    setDraftIdState(id);
  }, []);

  const setReferenceValues = useCallback((values: Record<string, unknown>) => {
    setReferenceValuesState(values);
  }, []);

  const setGroupVisibility = useCallback((visibility: string) => {
    setGroupVisibilityState(visibility);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({});
    setDraftIdState(undefined);
    setReferenceValuesState({});
  }, []);

  const hasData = Object.keys(formData).length > 0 || (formData.members && formData.members.length > 0) || false;

  return (
    <GroupChatFormContext.Provider
      value={{
        formData,
        updateFormData,
        members,
        setMembers,
        draftId,
        setDraftId,
        referenceValues,
        setReferenceValues,
        groupVisibility,
        setGroupVisibility,
        resetForm,
        hasData,
      }}
    >
      {children}
    </GroupChatFormContext.Provider>
  );
}

export function useGroupChatForm() {
  const context = useContext(GroupChatFormContext);
  if (context === undefined) {
    throw new Error("useGroupChatForm must be used within a GroupChatFormProvider");
  }
  return context;
}
